import { CONTACTUS_API } from "../hooks/Apis";
import {
  FaEnvelope,
  FaPhone,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaCalendarAlt,
  FaUser,
} from "react-icons/fa";
import { HiOutlineMail, HiOutlineChatAlt2 } from "react-icons/hi";
import React, { useState } from "react";
import { GlobalData } from "../assets/data/GlodalData";
import { Helmet } from "react-helmet-async";
import seoData from "../assets/data/seo.json";

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (honeypot) {
      console.warn("Bot detected! Submission blocked.");
      alert("Submission blocked. Please try again.");
      return;
    }

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      alert("Please fill in all required fields.");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(CONTACTUS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          honeypot: honeypot,
        }),
      });

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      if (result.success) {
        alert(
          "Appointment request submitted successfully! We'll get back to you within 24 hours."
        );
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setHoneypot("");
      } else {
        alert(result.error || "Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      if (error.name === "TypeError") {
        alert("Network error. Please check your connection and try again.");
      } else {
        alert("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const { title, description, keywords, canonical, ogImage } =
    seoData.contactus;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Cyber Space Digital" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-primary/90 text-white py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full mix-blend-screen filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/20 rounded-full mix-blend-screen filter blur-3xl opacity-20"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center justify-center gap-3 mb-6">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold tracking-widest uppercase">Contact Us</span>
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Start Your <span className="text-secondary">Wellness</span> Journey
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
            Book your personalized consultation with our experts and take the first step towards 
            your wellness transformation.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={`tel:${GlobalData.company.companyPhone}`}
              className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transform hover:-translate-y-1 transition-all duration-300 shadow-lg"
            >
              <FaPhone className="text-sm" />
              Call Now
            </a>
            <a
              href={GlobalData.company.companyPhone}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transform hover:-translate-y-1 transition-all duration-300 shadow-lg"
            >
              <FaWhatsapp className="text-sm" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Side - Contact Info */}
          <div className="lg:col-span-1">
            <div className="space-y-8 sticky top-8">
              {/* Contact Card */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-8 bg-primary rounded-full"></div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Contact Information
                  </h2>
                </div>
                
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Have questions about our services or ready to book your consultation? 
                  We're here to help you start your wellness journey.
                </p>

                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <HiOutlineMail className="text-primary text-xl" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Email</p>
                      <a
                        href={`mailto:${GlobalData.company.companyEmail}`}
                        className="text-gray-900 font-semibold hover:text-primary transition-colors"
                      >
                        {GlobalData.company.companyEmail}
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <FaPhone className="text-primary text-lg" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Phone</p>
                      <a
                        href={`tel:${GlobalData.company.companyPhone}`}
                        className="text-gray-900 font-semibold hover:text-primary transition-colors"
                      >
                        {GlobalData.company.companyPhone}
                      </a>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                      <FaWhatsapp className="text-secondary text-lg" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">WhatsApp</p>
                      <a
                        href={GlobalData.company.companyWhatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 font-semibold hover:text-secondary transition-colors"
                      >
                        Chat with us directly
                      </a>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Quick Connect
                  </h3>
                  <div className="flex flex-col gap-3">
                    <a
                      href={`mailto:${GlobalData.company.companyEmail}`}
                      className="flex items-center justify-center gap-3 bg-primary text-white px-4 py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors"
                    >
                      <FaEnvelope className="text-sm" />
                      Send Email
                    </a>
                    <a
                      href={`tel:${GlobalData.company.companyPhone}`}
                      className="flex items-center justify-center gap-3 bg-gray-900 text-white px-4 py-3 rounded-xl font-medium hover:bg-black transition-colors"
                    >
                      <FaPhone className="text-sm" />
                      Call Now
                    </a>
                    <a
                      href={GlobalData.company.companyWhatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-xl font-medium hover:from-green-600 hover:to-green-700 transition-colors"
                    >
                      <FaWhatsapp className="text-sm" />
                      WhatsApp Message
                    </a>
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <div className="bg-gradient-to-br from-primary to-primary/90 rounded-3xl p-8 text-white shadow-2xl">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <HiOutlineChatAlt2 className="text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold">Fast Response Time</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/90">Response Time</span>
                    <span className="font-bold">Under 2 Hours</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/90">Support Available</span>
                    <span className="font-bold">7 Days/Week</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/90">Satisfaction Rate</span>
                    <span className="font-bold">98%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form & Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Appointment Form */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-8 bg-secondary rounded-full"></div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Book Your Appointment
                </h2>
              </div>
              
              <p className="text-gray-600 mb-8 leading-relaxed">
                Fill out the form below and we'll get back to you within 24 hours 
                with a personalized consultation plan.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users */}
                <input
                  type="text"
                  name="honeypot"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ display: "none" }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      field: "name",
                      type: "text",
                      placeholder: "Your Full Name",
                      icon: <FaUser className="text-gray-400" />,
                    },
                    {
                      field: "email",
                      type: "email",
                      placeholder: "your@email.com",
                      icon: <FaEnvelope className="text-gray-400" />,
                    },
                    {
                      field: "phone",
                      type: "tel",
                      placeholder: "+91 00000 00000",
                      icon: <FaPhone className="text-gray-400" />,
                    },
                    {
                      field: "subject",
                      type: "text",
                      placeholder: "Consultation Type",
                      icon: <FaCalendarAlt className="text-gray-400" />,
                    },
                  ].map((item, idx) => (
                    <div key={idx} className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-2 capitalize">
                        {item.field}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                          {item.icon}
                        </div>
                        <input
                          type={item.type}
                          name={item.field}
                          placeholder={item.placeholder}
                          value={formData[item.field]}
                          onChange={handleChange}
                          className="w-full rounded-xl py-4 px-12 text-gray-900 bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                          required
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Message */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">
                    Message
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    name="message"
                    placeholder="Tell us about your concerns, preferred appointment time, or any specific questions you have..."
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="rounded-xl py-4 px-4 text-gray-900 bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    required
                  ></textarea>
                </div>

                {/* Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-4 rounded-xl font-semibold tracking-wide shadow-lg transition-all flex items-center justify-center gap-3 group ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary transform hover:-translate-y-0.5"
                  }`}
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending Request...
                    </>
                  ) : (
                    <>
                      <span className="text-white">Schedule Appointment</span>
                      <svg 
                        className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
                
                <p className="text-center text-gray-500 text-sm pt-4">
                  We respect your privacy. Your information is secure and will never be shared.
                </p>
              </form>
            </div>

            {/* Info Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: <FaPhone className="w-5 h-5 text-primary" />,
                  title: "Contact Details",
                  content: GlobalData.company.companyEmail,
                  phone: GlobalData.company.companyPhone,
                  bg: "bg-gradient-to-br from-primary/10 to-primary/5",
                },
                {
                  icon: <FaMapMarkerAlt className="w-5 h-5 text-primary" />,
                  title: "Clinic Location",
                  content: GlobalData.company.companyAddress,
                  bg: "bg-gradient-to-br from-secondary/10 to-secondary/5",
                },
                {
                  icon: <FaClock className="w-5 h-5 text-primary" />,
                  title: "Working Hours",
                  content: [
                    "Mon - Fri: 10:00 am - 8:00 pm",
                    "Saturday: 11:00 am - 5:00 pm",
                    "Sunday: Emergency Only",
                  ],
                  bg: "bg-gradient-to-br from-primary/10 to-primary/5",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`${item.bg} rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
                >
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  {item.phone ? (
                    <div className="space-y-2">
                      <a
                        href={`mailto:${item.content}`}
                        className="block text-gray-600 hover:text-primary text-sm font-medium transition-colors"
                      >
                        {item.content}
                      </a>
                      <a
                        href={`tel:${item.phone}`}
                        className="block text-gray-600 hover:text-primary text-sm font-medium transition-colors"
                      >
                        {item.phone}
                      </a>
                    </div>
                  ) : Array.isArray(item.content) ? (
                    <div className="text-gray-600 space-y-2">
                      {item.content.map((line, i) => (
                        <p key={i} className="text-sm font-medium">
                          {line}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm font-medium">{item.content}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Map Section */}
        <section className="mt-16">
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl border border-gray-100">
            <div className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-8 bg-secondary rounded-full"></div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Visit Our Clinic
                </h2>
              </div>
              <p className="text-gray-600 mb-8 max-w-2xl">
                Find us at our conveniently located clinic for personalized consultations 
                and treatments.
              </p>
            </div>
            
            <div className="relative h-96">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent z-10 pointer-events-none"></div>
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3800.5275272932704!2d83.30432737591525!3d17.719766093143583!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a39431b0e6fb1b1%3A0xe812b0d64305383!2sDermaSlim%20Wellness!5e0!3m2!1sen!2sin!4v1766679961761!5m2!1sen!2sin" 
              width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="DermaSlim Wellness Clinic Location"
                className="w-full h-full"></iframe>
              
             
            </div>
            
          
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactForm;